import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { getQueryParameterFromUri } from "@WhiteCap/Common/Utillities/utils";
import { OsnAccountDetailsModel } from "@WhiteCap/Services/OsnAccountDetailsService";
import asnSubscriptions from "@WhiteCap/Store/Reducer/WhiteCapReducer/OsnSubscriptionsDashboard/asnSusbcriptions";
import orderCompleteSubscriptions from "@WhiteCap/Store/Reducer/WhiteCapReducer/OsnSubscriptionsDashboard/orderCompleteSubscriptions";
import podSubscriptions from "@WhiteCap/Store/Reducer/WhiteCapReducer/OsnSubscriptionsDashboard/podSubscriptions";
import soaSubscriptions from "@WhiteCap/Store/Reducer/WhiteCapReducer/OsnSubscriptionsDashboard/SoaSubscriptions";
import { ApiResultModel as JobSitesApiResultModel } from "@WhiteCap/Store/Service/GetAllJobSitesService";
import {
    ApiResultModel,
    GetPhoneApiResultModel,
    PhoneModel,
} from "@WhiteCap/Store/Service/SubscriptionsDasboardService";
import { Draft } from "immer";

export interface SubscribedEmailDto {
    id?: string;
    email: string;
    isActive?: boolean;
    isSubscribedToReceiveSMS: boolean;
    phone: string;
    isEditing?: boolean;
    isSubscribedOrdersOnly?: boolean;
}

export type SubscriptionsModalType =
    | "soaOrderStatusSummary"
    | "podOrderStatusSummary"
    | "asnOrderStatusSummary"
    | "ocOrderStatusSummary";

export type SoaOrderStatusSummaryModel = {
    count?: number;
    isPrintPriceEnabled?: boolean;
    subscribedEmails?: SubscribedEmailDto[];
    properties?: {
        [key: string]: string;
    };
};

export type AsnOrderStatusSummaryModel = {
    count?: number;
    subscribedEmails?: SubscribedEmailDto[];
    properties?: {
        [key: string]: string;
    };
};

export type PodOrderStatusSummaryModel = {
    count?: number;
    frequency?: string;
    subscribedEmails?: SubscribedEmailDto[];
    properties?: {
        [key: string]: string;
    };
};

export type OcOrderStatusSummaryModel = {
    count?: number;
    subscribedEmails?: SubscribedEmailDto[];
    properties?: {
        [key: string]: string;
    };
};
interface SubscriptionDetailsModel {
    uri?: string;
    soaOrderStatusSummary?: SoaOrderStatusSummaryModel;
    asnOrderStatusSummary?: AsnOrderStatusSummaryModel;
    podOrderStatusSummary?: PodOrderStatusSummaryModel;
    ocOrderStatusSummary?: OcOrderStatusSummaryModel;
    parentId?: string;
    totalSites?: number;
    isParentSite?: boolean;
    properties?: {
        [key: string]: string;
    };
    companyName?: string;
    isFreshAccount?: boolean;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
}
export interface SubscriptionsDashboardState {
    isLoading: boolean;
    isFetchingSubscribers: boolean; // Used to track loading state of SOA/ASN/POD/OrderCompete modals
    isFetchingJobSites: boolean;
    osnTypeSelectionModalOpen: boolean;
    modifyingOsnTypeSelection: boolean;
    osnType: "accountLevel" | "individualJobSiteLevel";
    osnTypeSelectionConfirmed: boolean;
    currentSubscriptionCustomerId: string;
    accountLevelSubscriptions: SubscriptionDetailsModel;
    jobSiteSubscriptionsById: {
        [id: string]: SubscriptionDetailsModel;
    };
    jobSites: JobSitesApiResultModel;
    sessionBillToId: string;
    sessionShipToId: string;
    searchTerm: string;
    searchedJobSites: JobSitesApiResultModel;
    isUpgradeToBusinessAccountModalOpen: boolean;
    fetchJobSitesError: string;
    emailForWhichPhoneNumberIsBeingFetched: string;
    isApplyingSubscriptionsChanges: boolean;
    showAccountSelectionModal: boolean;
    accountDetails?: OsnAccountDetailsModel;
    accountSearchError?: string | null;
    isSearchingAccount: boolean;
}

const initialState: SubscriptionsDashboardState = {
    isLoading: false,
    isFetchingSubscribers: false,
    isFetchingJobSites: false,
    osnTypeSelectionModalOpen: false,
    modifyingOsnTypeSelection: false,
    osnType: "accountLevel",
    currentSubscriptionCustomerId: "",
    osnTypeSelectionConfirmed: false,
    jobSiteSubscriptionsById: {},
    accountLevelSubscriptions: {},
    sessionBillToId: "",
    sessionShipToId: "",
    searchTerm: "",
    jobSites: {
        sitesSummary: null,
        pagination: {
            page: 0,
            totalItemCount: 0,
        },
    },
    searchedJobSites: {
        sitesSummary: null,
        pagination: {
            page: 0,
            totalItemCount: 0,
        },
    },
    isUpgradeToBusinessAccountModalOpen: false,
    fetchJobSitesError: "",
    emailForWhichPhoneNumberIsBeingFetched: "",
    isApplyingSubscriptionsChanges: false,
    showAccountSelectionModal: false,
    accountDetails: undefined,
    accountSearchError: null,
    isSearchingAccount: false,
};

type CountByEmailModel = {
    [key: string]: PhoneModel;
};

const getCountByEmail = (emails: PhoneModel[]) => {
    const phoneByEmails: CountByEmailModel = {};
    emails.forEach(element => {
        phoneByEmails[element.email] = element;
    });
    return phoneByEmails;
};

const resetJobSitesState = (draft: Draft<SubscriptionsDashboardState>) => {
    draft.jobSites = {
        sitesSummary: null,
        pagination: {
            page: 0,
            totalItemCount: 0,
        },
    };
    draft.searchedJobSites = {
        sitesSummary: null,
        pagination: {
            page: 0,
            totalItemCount: 0,
        },
    };
    draft.searchTerm = "";
};

const SubscriptionsDashboard = {
    "Custom/SubscriptionDashboard/ModifyOsnTypeSelectionModalOpenState": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            value: boolean;
        },
    ) => {
        draft.osnTypeSelectionModalOpen = action.value;
        draft.modifyingOsnTypeSelection = true;
    },
    "Custom/SubscriptionDashboard/ToggleAccountSelectionModal": (
        draft: Draft<SubscriptionsDashboardState>,
        action: { value: boolean },
    ) => {
        draft.showAccountSelectionModal = action.value;
    },
    "Custom/SubscriptionDashboard/SetCurrentSubscriptionCustomerId": (
        draft: Draft<SubscriptionsDashboardState>,
        action: { value: string },
    ) => {
        draft.currentSubscriptionCustomerId = action.value;
    },
    "Custom/SubscriptionDashboard/ChangeNotificationType": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            value: "accountLevel" | "individualJobSiteLevel";
        },
    ) => {
        draft.osnType = action.value;
        draft.osnTypeSelectionConfirmed = true;
        if (action.value === "accountLevel") {
            const customerId = getQueryParameterFromUri(draft.accountLevelSubscriptions.uri, "customerId");
            draft.currentSubscriptionCustomerId = customerId;
        }
        draft.modifyingOsnTypeSelection = false;
    },
    "Custom/SubscriptionDashboard/BeginLoadData": (draft: Draft<SubscriptionsDashboardState>) => {
        draft.isLoading = true;
        draft.sessionBillToId = "";
        draft.sessionShipToId = "";
    },
    "Custom/SubscriptionDashboard/LoadDataError": (draft: Draft<SubscriptionsDashboardState>) => {
        draft.isLoading = false;
    },
    "Custom/SubscriptionDashboard/CompleteLoadData": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            result: ApiResultModel;
        },
    ) => {
        const customerId = getQueryParameterFromUri(action.result?.uri, "customerId");
        const previousCustomerId = draft.currentSubscriptionCustomerId;
        const shouldResetJobSites =
            action.result.isFreshAccount || !customerId || (customerId && customerId !== previousCustomerId);

        if (shouldResetJobSites) {
            resetJobSitesState(draft);
        }

        if (!customerId || action.result.isFreshAccount) {
            draft.accountLevelSubscriptions = action.result;
            draft.isLoading = false;
            return;
        }

        if (draft.osnType === "accountLevel") {
            draft.currentSubscriptionCustomerId = customerId;
            draft.accountLevelSubscriptions = action.result;
        } else {
            draft.currentSubscriptionCustomerId = customerId;
            draft.jobSiteSubscriptionsById[customerId] = action.result;
        }
        draft.isLoading = false;
    },
    "Custom/SubscriptionDashboard/BeginLoadAllJobSites": (draft: Draft<SubscriptionsDashboardState>) => {
        draft.isFetchingJobSites = true;
    },
    "Custom/SubscriptionDashboard/LoadAllJobSitesError": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            error: string;
        },
    ) => {
        draft.isFetchingJobSites = false;
        draft.fetchJobSitesError = action.error;
    },
    "Custom/SubscriptionDashboard/CompleteLoadAllJobSites": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            result: JobSitesApiResultModel;
        },
    ) => {
        if (!action.result.sitesSummary) {
            draft.isFetchingJobSites = false;
            return;
        }
        const filter = getQueryParameterFromUri(action.result?.uri, "filter");
        const existingSitesSummary = draft.jobSites?.sitesSummary || [];
        const existingSearchedSitesSummary = draft.searchedJobSites?.sitesSummary || [];
        const summaryToBeUpdated = filter ? existingSearchedSitesSummary : existingSitesSummary;
        const isFirstPage = action.result.pagination.currentPage === 1;
        const data = {
            sitesSummary: isFirstPage
                ? action.result.sitesSummary
                : [...summaryToBeUpdated, ...action.result.sitesSummary],
            uri: action.result.uri,
            properties: action.result.properties,
            pagination: action.result.pagination,
        };
        if (filter) {
            draft.searchedJobSites = data;
        } else {
            draft.jobSites = data;
        }

        draft.isFetchingJobSites = false;
    },
    "Custom/SubscriptionDashboard/UpdateSearchTerm": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            value: string;
        },
    ) => {
        draft.searchTerm = action.value;
        if (!action.value) {
            draft.searchedJobSites = initialState.searchedJobSites;
        }
    },
    "Custom/SubscriptionDashboard/ResetData": (draft: Draft<SubscriptionsDashboardState>) => {
        draft = initialState;
    },
    "Custom/SubscriptionDashboard/ModifyUpgradeToBusinessModelOpenState": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            value: boolean;
        },
    ) => {
        draft.isUpgradeToBusinessAccountModalOpen = action.value;
    },
    "Custom/SubscriptionDashboard/BeginFetchPhoneNumber": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            emailId: string;
        },
    ) => {
        draft.emailForWhichPhoneNumberIsBeingFetched = action.emailId;
    },
    "Custom/SubscriptionDashboard/SoaSubscriptions/FetchPhoneNumberError": (
        draft: Draft<SubscriptionsDashboardState>,
    ) => {
        draft.emailForWhichPhoneNumberIsBeingFetched = "";
    },
    "Custom/SubscriptionDashboard/CompleteFetchPhoneNumber": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            result: GetPhoneApiResultModel;
            subscriptionModalType: SubscriptionsModalType;
        },
    ) => {
        draft.emailForWhichPhoneNumberIsBeingFetched = "";
        if (draft.osnType === "accountLevel") {
            const emails = draft.accountLevelSubscriptions[action.subscriptionModalType]?.subscribedEmails || [];
            const phoneByEmails = getCountByEmail(action.result);
            const updatedEmails = emails.map(emailObj => {
                if (phoneByEmails[emailObj.email]) {
                    return {
                        ...emailObj,
                        phone: phoneByEmails[emailObj.email].phone,
                    };
                }
                return emailObj;
            });
            draft.accountLevelSubscriptions[action.subscriptionModalType] = {
                ...draft.accountLevelSubscriptions[action.subscriptionModalType],
                subscribedEmails: updatedEmails,
            };
        } else if (draft.osnType === "individualJobSiteLevel" && draft.currentSubscriptionCustomerId) {
            const emails =
                draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId][action.subscriptionModalType]
                    ?.subscribedEmails || [];
            const phoneByEmails = getCountByEmail(action.result);
            const updatedEmails = emails.map(emailObj => {
                if (phoneByEmails[emailObj.email]) {
                    return {
                        ...emailObj,
                        phone: phoneByEmails[emailObj.email].phone,
                    };
                }
                return emailObj;
            });
            draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId][action.subscriptionModalType] = {
                ...draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId][action.subscriptionModalType],
                subscribedEmails: updatedEmails,
            };
        }
    },
    "Custom/SubscriptionDashboard/AccountSearch/Begin": (draft: Draft<SubscriptionsDashboardState>) => {
        draft.isSearchingAccount = true;
        draft.accountSearchError = null;
    },
    "Custom/SubscriptionDashboard/AccountSearch/Success": (
        draft: Draft<SubscriptionsDashboardState>,
        action: { accountDetails: OsnAccountDetailsModel },
    ) => {
        draft.isSearchingAccount = false;
        draft.accountDetails = action.accountDetails;
        draft.accountSearchError = null;
    },
    "Custom/SubscriptionDashboard/AccountSearch/Failed": (
        draft: Draft<SubscriptionsDashboardState>,
        action: { errorMessage: string },
    ) => {
        draft.isSearchingAccount = false;
        draft.accountSearchError = action.errorMessage;
    },
    "Custom/SubscriptionDashboard/AccountSearch/Complete": (draft: Draft<SubscriptionsDashboardState>) => {
        draft.isSearchingAccount = false;
    },
    ...soaSubscriptions,
    ...podSubscriptions,
    ...asnSubscriptions,
    ...orderCompleteSubscriptions,
};

export default createTypedReducerWithImmer(initialState, SubscriptionsDashboard);
